/**
 * What to say after seeding HSN memory (finding A5, 2026-09-28).
 *
 * The claim to get right is **what "learned" means**. A seeded code is not
 * applied to anything and nobody confirmed it: it is PROPOSED, under
 * `hsn.from_history`, on lines whose heading is too short for this client's
 * turnover (the one place `enrich` reads the memory) — a reviewer still
 * accepts or declines each one. It also takes the product off the download
 * queue (`hsn/queue.py` skips every remembered product). A sentence saying codes were "set" or
 * "applied" would be false, and would read to a practitioner as a bulk change
 * to the return. So the sentence says proposed, and says what was skipped and
 * why, because the skips are the seed's whole caution.
 *
 * **And `seeded` is not "new".** It counts every product the registers answer,
 * already remembered or not — the seed rewrites the answers it stands behind.
 * The first wording, "Learned the code for 2,924 products", was pressed on a
 * client whose 2,924 answers were already remembered, and learned nothing; the
 * sentence now says what the number is.
 */

import type { HsnSeed } from './api'

function count(n: number, one: string, many: string): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`
}

export function seedSummary(result: HsnSeed): string {
  const parts: string[] = [
    result.seeded > 0
      ? `This client's own registers consistently code ${count(result.seeded, 'product', 'products')}, ` +
        'and those codes are now remembered. They stay out of the download queue and are proposed ' +
        'wherever a line carries a heading too short for this client — nothing is applied until a ' +
        'reviewer accepts it.'
      : "This client's own registers do not consistently code any product yet.",
  ]
  const skipped: string[] = []
  if (result.skipped_ambiguous > 0) skipped.push(`${count(result.skipped_ambiguous, 'product', 'products')} coded two ways`)
  if (result.skipped_contradictory > 0)
    skipped.push(`${count(result.skipped_contradictory, 'product', 'products')} coded under unrelated chapters`)
  if (result.skipped_short > 0) skipped.push(`${count(result.skipped_short, 'product', 'products')} with only a heading`)
  if (result.skipped_decided > 0)
    skipped.push(`${count(result.skipped_decided, 'product', 'products')} a person already decided`)
  if (skipped.length > 0) parts.push(`Left alone: ${skipped.join('; ')}.`)
  if (result.withdrawn > 0)
    parts.push(
      `${count(result.withdrawn, 'earlier proposal was', 'earlier proposals were')} withdrawn — ` +
        'the registers no longer support ' +
        (result.withdrawn === 1 ? 'it.' : 'them.'),
    )
  return parts.join(' ')
}
